import { Save, Sparkles, RefreshCw, Printer, FileSpreadsheet } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MONTH_OPTIONS } from "@/lib/mdcf";
import StickyActionBar from "@/components/mobile/StickyActionBar";

// Mobile-only Monthly MDCF layout. Visible <md (768px). Compact card view
// for the commonly-edited fields + Save / Re-autofill / Print Odia / Print
// English actions. The full preview lives in the desktop block and is
// hidden on phones because the dense govt-bill forms simply don't fit.
function NumField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">{label}</span>
      <input
        data-testid={testId}
        type="number"
        inputMode="numeric"
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-lg border border-border bg-white px-2.5 py-2 text-right font-mono text-sm text-ink-900 outline-none focus:border-clay focus:ring-1 focus:ring-clay/30"
      />
    </label>
  );
}
function TxtField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">{label}</span>
      <input
        data-testid={testId}
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-lg border border-border bg-white px-2.5 py-2 text-sm text-ink-900 outline-none focus:border-clay focus:ring-1 focus:ring-clay/30"
      />
    </label>
  );
}

export default function MobileMonthlyMDCF(props) {
  const {
    active, year, month, setYear, setMonth, yearChoices,
    data, set,
    dailyEntries,
    applyAutoFill, handleSave, handleReset, doPrint,
  } = props;
  const general = data?.general || {};
  const wd = data?.monthly?.section2?.totalWorkingDays || {};

  const monthLabel = MONTH_OPTIONS.find((m) => m.v === month)?.en || month;

  return (
    <div className="md:hidden" data-testid="mobile-mdcf">
      {/* Hero */}
      <header className="px-5 pt-4">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-clay">Monthly MDCF</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-ink-900">Monthly Data Capture</h1>
        <p className="mt-2 text-sm text-ink-700">
          <b className="text-ink-900">{active?.schoolName || "—"}</b>
          <span className="mx-1.5 text-ink-500">•</span>
          UDISE: {active?.udiseCode || "—"}
        </p>
      </header>

      {/* Month picker + auto-fill hint */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-mdcf-controls">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Year</span>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="m-mdcf-year" className="mt-1 h-10 border-border bg-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{yearChoices.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Month</span>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="m-mdcf-month" className="mt-1 h-10 border-border bg-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTH_OPTIONS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.en}</SelectItem>)}</SelectContent>
            </Select>
          </label>
        </div>
        <p className="mt-3 rounded-lg bg-moss/10 px-3 py-2 text-[0.7rem] text-moss-dark" data-testid="m-mdcf-autofill-hint">
          Loaded <b>{(dailyEntries || []).length}</b> daily {(dailyEntries || []).length === 1 ? "entry" : "entries"} for <b>{monthLabel} {year}</b>.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={applyAutoFill} data-testid="m-mdcf-autofill" className="flex items-center justify-center gap-1.5 rounded-xl border border-clay/30 bg-white py-2.5 text-xs font-bold text-clay active:bg-sand-50">
            <Sparkles className="h-3.5 w-3.5" /> Re-autofill
          </button>
          <button onClick={handleReset} data-testid="m-mdcf-reset" className="flex items-center justify-center gap-1.5 rounded-xl border border-border bg-white py-2.5 text-xs font-bold text-ink-700 active:bg-sand-50">
            <RefreshCw className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
      </section>

      {/* Quick Edit — General */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-mdcf-general">
        <p className="font-heading text-base font-bold text-ink-900">Quick Edit — General</p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <TxtField testId="m-mdcf-school-serial"  label="School Serial No." value={general.schoolSerialNo}   onChange={(v) => set("general.schoolSerialNo", v)} />
          <NumField testId="m-mdcf-total-children" label="Total Children"    value={general.totalChildren}    onChange={(v) => set("general.totalChildren", v)} />
          <TxtField testId="m-mdcf-kitchen-type"   label="Kitchen Type"      value={general.kitchenType}      onChange={(v) => set("general.kitchenType", v)} />
          <NumField testId="m-mdcf-enrolment"      label="Enrolment (total)" value={general.enrolment}        onChange={(v) => set("general.enrolment", v)} />
          <TxtField testId="m-mdcf-ngo-shg"        label="NGO / SHG"         value={general.ngoShg}           onChange={(v) => set("general.ngoShg", v)} />
          <TxtField testId="m-mdcf-state"          label="State / UT"        value={general.stateUT}          onChange={(v) => set("general.stateUT", v)} />
        </div>
      </section>

      {/* Working Days */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-mdcf-working-days">
        <p className="font-heading text-base font-bold text-ink-900">Working Days — Section 3</p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <NumField testId="m-mdcf-wd-bv"  label="Bal Vatika"    value={wd.balBatika}          onChange={(v) => set("monthly.section2.totalWorkingDays.balBatika", v)} />
          <NumField testId="m-mdcf-wd-pri" label="Primary"       value={wd.primaryClass}       onChange={(v) => set("monthly.section2.totalWorkingDays.primaryClass", v)} />
          <NumField testId="m-mdcf-wd-up"  label="Upper Primary" value={wd.upperPrimaryClass}  onChange={(v) => set("monthly.section2.totalWorkingDays.upperPrimaryClass", v)} />
          <NumField testId="m-mdcf-wd-hs"  label="High School"   value={wd.upperSchool}        onChange={(v) => set("monthly.section2.totalWorkingDays.upperSchool", v)} />
        </div>
      </section>

      {/* Health & Inspection */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-mdcf-health">
        <p className="font-heading text-base font-bold text-ink-900">Health &amp; Inspection</p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <NumField testId="m-mdcf-ifa-b" label="IFA Boys"          value={data?.eng?.healthStatus?.ifaBoys}              onChange={(v) => set("eng.healthStatus.ifaBoys", v)} />
          <NumField testId="m-mdcf-ifa-g" label="IFA Girls"         value={data?.eng?.healthStatus?.ifaGirls}             onChange={(v) => set("eng.healthStatus.ifaGirls", v)} />
          <NumField testId="m-mdcf-rbsk-s" label="RBSK Screened"    value={data?.eng?.healthStatus?.rbskScreened}         onChange={(v) => set("eng.healthStatus.rbskScreened", v)} />
          <NumField testId="m-mdcf-rbsk-r" label="RBSK Referred"    value={data?.eng?.healthStatus?.rbskReferred}         onChange={(v) => set("eng.healthStatus.rbskReferred", v)} />
          <NumField testId="m-mdcf-insp-tf" label="By Task Force"   value={data?.eng?.inspection?.byTaskForce}            onChange={(v) => set("eng.inspection.byTaskForce", v)} />
          <NumField testId="m-mdcf-insp-d"  label="By District"     value={data?.eng?.inspection?.byDistrictOfficials}    onChange={(v) => set("eng.inspection.byDistrictOfficials", v)} />
          <NumField testId="m-mdcf-insp-b"  label="By Block"        value={data?.eng?.inspection?.byBlockOfficials}       onChange={(v) => set("eng.inspection.byBlockOfficials", v)} />
          <NumField testId="m-mdcf-insp-smc" label="By SMC"         value={data?.eng?.inspection?.bySMCMembers}           onChange={(v) => set("eng.inspection.bySMCMembers", v)} />
        </div>
      </section>

      {/* Print actions */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-mdcf-print">
        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-ink-700">Print bill</p>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button onClick={() => doPrint("odia")} data-testid="m-mdcf-print-odia" className="flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 py-3 text-[0.7rem] font-bold text-white active:scale-[0.99]">
            <Printer className="h-3.5 w-3.5" /> Odia
          </button>
          <button onClick={() => doPrint("odia2")} data-testid="m-mdcf-print-odia2" className="flex items-center justify-center gap-1.5 rounded-xl bg-clay py-3 text-[0.7rem] font-bold text-white active:scale-[0.99]">
            <Printer className="h-3.5 w-3.5" /> Odia Fmt 2
          </button>
          <button onClick={() => doPrint("eng")} data-testid="m-mdcf-print-eng" className="flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 py-3 text-[0.7rem] font-bold text-white active:scale-[0.99]">
            <FileSpreadsheet className="h-3.5 w-3.5" /> English
          </button>
        </div>
        <p className="mt-2 text-[0.65rem] text-ink-700">
          Tip: prints use A4 landscape from the device's print dialog. Save as PDF from there.
        </p>
      </section>

      <div className="h-44" aria-hidden />

      {/* iter146 — Sticky Save via shared StickyActionBar. */}
      <StickyActionBar
        testId="m-mdcf-save"
        onClick={handleSave}
      >
        <Save className="h-4 w-4" /> Save MDCF
      </StickyActionBar>
    </div>
  );
}
